import { useState } from 'react';
import { TapUser, LeaderboardEntry } from '@/domain/types';
import { useLeaderboard } from '@/hooks/useLeaderboard';
import { XQUEST_LEADERBOARD_URL, XQUEST_WEB_URL } from '@/domain/constants';
import { tapApi } from '@/services/tapApi';
import { PrizePoolBanner } from './PrizePoolBanner';

interface ProgressScreenProps {
  user: TapUser | null;
}

export function ProgressScreen({ user }: ProgressScreenProps) {
  const [leaderboard, refreshLeaderboard] = useLeaderboard();
  const [isLinkingWallet, setIsLinkingWallet] = useState(false);

  if (!user) return null;

  const handleLinkWallet = async () => {
    setIsLinkingWallet(true);
    try {
      const { url } = await tapApi.createWalletLinkToken();
      window.Telegram?.WebApp?.openLink(url);
    } catch (err) {
      console.error('Failed to create wallet link token:', err);
      // Fallback: open site without token
      window.Telegram?.WebApp?.openLink(XQUEST_WEB_URL);
    } finally {
      setIsLinkingWallet(false);
    }
  };

  if (!user) return null;

  // Dynamic font size for large numbers
  const getPointsFontSize = (pts: number): string => {
    if (pts >= 1000000) return 'text-lg';   // 1,000,000+ → 18px
    if (pts >= 100000) return 'text-xl';    // 100,000+ → 20px
    return 'text-2xl';                       // < 100,000 → 24px
  };

  const getRankFontSize = (rank: number): string => {
    if (rank >= 10000) return 'text-lg';    // 10,000+ → 18px
    if (rank >= 1000) return 'text-xl';     // 1,000+ → 20px
    return 'text-2xl';                       // < 1,000 → 24px
  };

  const getMedalEmoji = (rank: number): string | null => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  // Top 10 entries
  const top10 = leaderboard.entries.slice(0, 10);

  // Check if user is in top 10
  const userInTop10 = user.rank <= 10;

  return (
    <div className="flex h-full flex-col overflow-auto p-5 pb-24">
      {/* Screen Title */}
      <h1 className="mb-4 text-2xl font-bold uppercase tracking-wider text-white">
        Progress
      </h1>

      {/* Prize Pool */}
      <PrizePoolBanner />

      {/* My Stats */}
      <div className="mb-6 flex gap-3">
        <div
          className="flex flex-1 flex-col items-center gap-1.5 rounded p-4"
          style={{
            background: 'rgba(23, 37, 84, 0.3)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(6, 182, 212, 0.3)',
          }}
        >
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            Your Rank
          </span>
          <span className={`${getRankFontSize(user.rank)} font-extrabold text-glow-cyan glow-cyan-sm`}>
            #{user.rank.toLocaleString()}
          </span>
        </div>
        <div
          className="flex flex-1 flex-col items-center gap-1.5 rounded p-4"
          style={{
            background: 'rgba(23, 37, 84, 0.3)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(6, 182, 212, 0.3)',
          }}
        >
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            Your Points
          </span>
          <span className={`${getPointsFontSize(user.points)} font-extrabold text-glow-cyan glow-cyan-sm`}>
            {user.points.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Wallet Status */}
      {user.wallet ? (
        <div className="mb-6 flex items-center gap-2 text-sm text-gray-400">
          <span className="h-2 w-2 rounded-full bg-green-500" />
          <span>
            Wallet: {user.wallet.slice(0, 6)}...{user.wallet.slice(-4)}
          </span>
        </div>
      ) : (
        <button
          onClick={handleLinkWallet}
          disabled={isLinkingWallet}
          className="mb-6 w-full rounded py-3 text-sm font-medium transition-colors disabled:opacity-50"
          style={{
            background: 'rgba(0, 162, 255, 0.15)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(0, 212, 255, 0.4)',
            color: '#00d4ff',
          }}
        >
          {isLinkingWallet ? 'Loading...' : 'Link Wallet for Rewards →'}
        </button>
      )}

      {leaderboard.isLoading ? (
        <div className="flex justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
        </div>
      ) : leaderboard.error ? (
        <p className="text-center text-sm text-red-400">{leaderboard.error}</p>
      ) : (
        <>
          {/* Top Players Section */}
          <section className="mb-6">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-glow-cyan">
                <span>🏆</span> Top Players
              </h2>
              <button
                onClick={refreshLeaderboard}
                className="text-xs text-cyan-400 hover:underline"
              >
                Refresh
              </button>
            </div>

            <div className="flex flex-col gap-2">
              {top10.map((entry) => (
                <LeaderboardRow
                  key={entry.rank}
                  entry={entry}
                  isMe={entry.rank === user.rank}
                  medal={entry.rank <= 3 ? getMedalEmoji(entry.rank) : null}
                />
              ))}
            </div>
          </section>

          {/* Around You Section - only show if user is not in top 10 */}
          {!userInTop10 && leaderboard.aroundMe.length > 0 && (
            <section className="mb-6">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-glow-cyan">
                <span>👤</span> Around You
              </h2>

              <div className="flex flex-col gap-2">
                {leaderboard.aroundMe.map((entry) => (
                  <LeaderboardRow
                    key={entry.rank}
                    entry={entry}
                    isMe={entry.rank === user.rank}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Full leaderboard link */}
          <button
            onClick={() => window.Telegram?.WebApp?.openLink(XQUEST_LEADERBOARD_URL)}
            className="w-full rounded py-3 text-sm font-medium transition-colors"
            style={{
              background: 'rgba(0, 162, 255, 0.15)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(0, 212, 255, 0.4)',
              color: '#00d4ff',
            }}
          >
            View Full Leaderboard →
          </button>
        </>
      )}
    </div>
  );
}

interface LeaderboardRowProps {
  entry: LeaderboardEntry;
  isMe: boolean;
  medal?: string | null;
}

function LeaderboardRow({ entry, isMe, medal }: LeaderboardRowProps) {
  return (
    <div
      className="flex items-center gap-3 rounded p-3.5 transition-all"
      style={{
        background: isMe
          ? 'linear-gradient(135deg, rgba(0, 162, 255, 0.2), rgba(6, 182, 212, 0.2))'
          : 'rgba(23, 37, 84, 0.2)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: isMe
          ? '1px solid rgba(6, 182, 212, 0.5)'
          : '1px solid rgba(6, 182, 212, 0.2)',
        boxShadow: isMe ? '0 0 20px rgba(6, 182, 212, 0.2)' : 'none',
      }}
    >
      {/* Rank */}
      <div className="min-w-[40px] text-sm font-semibold text-gray-400">
        {medal ? <span className="text-xl">{medal}</span> : `#${entry.rank}`}
      </div>

      {/* Name */}
      <div className="flex flex-1 items-center gap-2 overflow-hidden">
        <div className="flex-1 min-w-0">
          {entry.tgUsername ? (
            <>
              <span className="block truncate text-sm font-medium text-white">@{entry.tgUsername}</span>
              {entry.wallet && (
                <span className="block truncate text-[10px] font-mono text-gray-500">
                  {entry.wallet.slice(0, 6)}...{entry.wallet.slice(-4)}
                </span>
              )}
            </>
          ) : (
            <span className="block truncate text-sm font-medium text-white">{entry.displayName}</span>
          )}
        </div>
        {isMe && (
          <span
            className="flex-shrink-0 rounded-sm px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white"
            style={{
              background: 'linear-gradient(135deg, #00a2ff, #06b6d4)',
            }}
          >
            You
          </span>
        )}
      </div>

      {/* Points */}
      <div className="flex-shrink-0 text-sm font-semibold text-cyan-400">
        {entry.points.toLocaleString()}
      </div>
    </div>
  );
}
